import React, { useState, useEffect } from 'react';
import { useAuth, INACTIVE_NOTICE_STORAGE_KEY } from '../contexts/AuthContext';

const logoUrl = process.env.PUBLIC_URL + '/logo.png';

export default function Login() {
  const { signIn, resetPasswordForEmail } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  useEffect(() => {
    try {
      const notice = sessionStorage.getItem(INACTIVE_NOTICE_STORAGE_KEY);
      if (notice) {
        setError(notice);
        sessionStorage.removeItem(INACTIVE_NOTICE_STORAGE_KEY);
      }
    } catch {
      // ignore
    }
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    try {
      await signIn({ email, password });
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotSubmit(e) {
    e.preventDefault();
    if (!email.trim()) {
      setError('Enter your email address.');
      return;
    }
    setError('');
    setMessage('');
    setLoading(true);
    try {
      await resetPasswordForEmail(email.trim());
      setMessage('Check your email for the password reset link.');
    } catch (err) {
      const status = err?.status;
      const rawMessage = err?.message || '';
      const normalized = String(rawMessage || '').toLowerCase();
      const isInternal = status === 500 || normalized.includes('internal') || normalized === '500' || normalized.includes(' http 500');
      const msg = isInternal
        ? 'Could not send reset link right now. Please try again in a few minutes.'
        : rawMessage || 'Could not send reset link.';
      setError(msg);
      if (process.env.NODE_ENV === 'development' && (status || rawMessage)) {
        console.warn('Reset password error:', { status, message: rawMessage || err });
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-screen">
      <div className="login-bg-gradient" aria-hidden />
      <div className="login-card">
        <div className="login-logo-wrap">
          <img src={logoUrl} alt="CDT" className="login-logo" />
        </div>
        {showForgot ? (
          <>
            <h1 className="login-title">Reset password</h1>
            <p className="login-subtitle">Enter your email and we’ll send you a link to reset your password.</p>
            <form className="login-form" onSubmit={handleForgotSubmit}>
              <div className="login-field">
                <label htmlFor="login-forgot-email">Email</label>
                <input
                  id="login-forgot-email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className={error ? 'input-error' : ''}
                />
              </div>
              {error && <p className="login-error">{error}</p>}
              {message && <p className="login-message">{message}</p>}
              <button type="submit" className="login-submit" disabled={loading}>
                {loading ? <span className="login-spinner" aria-hidden /> : 'Send reset link'}
              </button>
              <button
                type="button"
                className="login-forgot"
                style={{ marginTop: '0.75rem', display: 'block', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted, #666)', fontSize: '0.9rem' }}
                onClick={() => { setShowForgot(false); setError(''); setMessage(''); }}
              >
                Back to sign in
              </button>
            </form>
          </>
        ) : (
          <>
            <h1 className="login-title">Welcome back</h1>
            <p className="login-subtitle">Sign in to your account</p>
            <form className="login-form" onSubmit={handleSubmit}>
              <div className="login-field">
                <label htmlFor="login-email">Email</label>
                <input
                  id="login-email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className={error ? 'input-error' : ''}
                />
              </div>
              <div className="login-field">
                <label htmlFor="login-password">Password</label>
                <div className="login-password-wrap">
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    autoComplete="current-password"
                    className={error ? 'input-error' : ''}
                  />
                  <button
                    type="button"
                    className="login-toggle-password"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? '🙈' : '👁'}
                  </button>
                </div>
              </div>
              {error && <p className="login-error">{error}</p>}
              {message && <p className="login-message">{message}</p>}
              <div className="login-options">
                <label className="login-remember">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                  />
                  Remember me
                </label>
                <button type="button" className="login-forgot" onClick={() => setShowForgot(true)}>
                  Forgot password?
                </button>
              </div>
              <button type="submit" className="login-submit" disabled={loading}>
                {loading ? (
                  <span className="login-spinner" aria-hidden />
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
